import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { BasketballSeasonPlanerGame, GymBasic } from '@hefe/models';
import {
  BasketballGameActions,
  BasketballGamesSlice,
  BasketballRefereesSlice,
  CarsSlice,
  ClubSlice,
  ModalsSlice,
  TasksActions,
  currentUserSelector,
} from '@hefe/store';
import { colors } from '@hefe/ui';
import { clubHasSubscription, formatDate } from '@hefe/utils';
import { Alert, Button, Col, Divider, Row, Select, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomList from '../../../core/components/custom-list/custom-list.component';
import SeasonPlanerListItemComponent from '../../components/gameday-planer-list-item/gameday-planer-list-item.component';
import { WarningOutlined } from '@ant-design/icons';

export default function GamedayPlanerPage() {
  const dispatch = useAppDispatch();
  const seasonPlanerState = useSelector(
    BasketballGamesSlice.basketballSeasonPlanerSelector
  );

  const user = useSelector(currentUserSelector);
  const tasks = useSelector(ClubSlice.clubTasksSelector);

  const currentWeek = seasonPlanerState.currentWeek;
  let weekData = {};
  if (currentWeek) {
    weekData = seasonPlanerState.games[currentWeek.weekAndYear] || [];
  }

  const [savingTaskIds, setSavingTaskIds] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);

  useEffect(() => {
    fetchData('CURRENT');
    fetchReferees();
    fetchCars();
    fetchTasks();
  }, []);

  useEffect(() => {
    if (!seasonPlanerState.visible_tasks) {
      return;
    }

    const taskIds = seasonPlanerState.visible_tasks?.map(
      (task) => task.value
    ) as number[];

    if (taskIds) {
      setSelectedTaskIds(taskIds);
    }
  }, [seasonPlanerState.visible_tasks]);

  const tasksSelectOptions = useMemo(() => {
    if (!tasks) {
      return [];
    }

    return tasks.map((task) => ({
      label: task.name,
      value: task.id,
    }));
  }, [tasks]);

  const fetchData = (
    mode: 'CURRENT' | 'NEXT' | 'PREVIOUS' | 'CUSTOM',
    year?: number,
    week?: number
  ) => {
    let requestData = undefined;
    switch (mode) {
      case 'NEXT':
        requestData = seasonPlanerState.pagination.next_week;
        break;
      case 'PREVIOUS':
        requestData = seasonPlanerState.pagination.last_week;
        break;
      case 'CUSTOM':
        requestData = {
          weekOfYear: week,
          year: year,
        };
        break;
    }

    dispatch(
      BasketballGamesSlice.fetchBasketballSeasonPlanerGames({
        weekOfYear: requestData?.weekOfYear,
        year: requestData?.year,
      })
    );
  };

  const fetchReferees = () => {
    dispatch(
      BasketballRefereesSlice.fetchBasketballRefereesSelect({
        cacheDuration: 3 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => null);
  };

  const fetchCars = () => {
    if (!clubHasSubscription(AuthSubscriptions.second, user)) {
      return;
    }
    dispatch(
      CarsSlice.fetchCars({
        cacheDuration: 5 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => null);
  };

  const fetchTasks = () => {
    dispatch(TasksActions.fetchClubTasks())
      .unwrap()
      .catch(() => null);
  };

  const onNextButtonClick = () => {
    fetchData('NEXT');
  };

  const onCurrentButtonClick = () => {
    fetchData('CURRENT');
  };

  const onPreviousButtonClick = () => {
    fetchData('PREVIOUS');
  };

  const onWeekSelect = (value: string) => {
    const valueArray = value.split('-');
    const year = parseInt(valueArray[0]);
    const week = parseInt(valueArray[1]);
    fetchData('CUSTOM', year, week);
  };

  const onGameClick = (game: BasketballSeasonPlanerGame) => {
    dispatch(
      ModalsSlice.modalsActions.openGameModal({
        id: game.id,
      })
    );
  };

  const updateSelectedTaskIds = () => {
    const initalTaskIds = seasonPlanerState.visible_tasks?.map(
      (task) => task.value
    ) as number[];

    if (
      initalTaskIds.length === selectedTaskIds.length &&
      initalTaskIds.every((value, index) => value === selectedTaskIds[index])
    ) {
      return;
    }

    setSavingTaskIds(true);
    dispatch(
      BasketballGameActions.setBasketballSeasonPlanerVisibleTasks({
        task_ids: selectedTaskIds,
      })
    )
      .unwrap()
      .then(() => {
        fetchData('CURRENT');
      })
      .catch(() => null)
      .finally(() => {
        setSavingTaskIds(false);
      });
  };


  const renderDateAlert = (gyms: GymBasic[]) => {
    const gymList = gyms.map(g => g.name).join(", ");
    return(
      <Alert icon={<WarningOutlined />} 
      type="warning"
      banner
      message={<Typography.Text strong>{t('season_planer.gym_closures.title')}</Typography.Text>}
      description={t('season_planer.gym_closures.description', {gyms: gymList})} />
    )
  }

  const renderDateDivider = (date: string) => {
    const formattedDate = formatDate(date, 'short');
    return (
      <Divider
        orientation="center"
        style={{ fontSize: 20, borderColor: colors.primary }}
      >
        {formattedDate}
      </Divider>
    );
  };

  const renderGymDivider = (name: string) => {
    return (
      <Divider orientation="left" style={{ borderColor: colors.secondary }}>
        {name}
      </Divider>
    );
  };

  const renderWeekOfYears = () => {
    const weeks = seasonPlanerState.availableWeeks;
    const currentWeek = seasonPlanerState.currentWeek?.weekAndYear ?? '';
    const selectOptions = weeks.map((week) => {
      const startOfWeek = dayjs()
        .year(week.year)
        .week(week.week)
        .startOf('week')
        .format('DD.MM.');
      const endOfWeek = dayjs()
        .year(week.year)
        .week(week.week)
        .endOf('week')
        .format('DD.MM.');
      return {
        label: `${t('week')} ${week.week} | ${startOfWeek} - ${endOfWeek}${
          week.year
        } `,
        value: `${week.year}-${week.week}`,
      };
    });

    return (
      <Select
        disabled={seasonPlanerState.loading}
        value={currentWeek}
        options={selectOptions}
        onSelect={(value, option) => onWeekSelect(option.value)}
      />
    );
  };

  const renderDays = () => {
    if (!currentWeek?.weekAndYear) {
      return;
    }

    const output = [];
    const gamesOfWeek = seasonPlanerState.games[currentWeek?.weekAndYear];
    const days = Object.keys(gamesOfWeek);
    const gymClosuresOfWeek = seasonPlanerState.gym_closures[currentWeek?.weekAndYear];

    for (const day of days) {
      output.push(renderDateDivider(day));
      if (gymClosuresOfWeek[day]) {
        output.push(renderDateAlert(gymClosuresOfWeek[day]));
      }

      const gyms = gamesOfWeek[day];
      const gymKeys = Object.keys(gyms);
      // Put away games to end of page
      gymKeys.sort((a, b) => {
        if (a === '') return 1;
        else if (b === '') return -1;
        else {
          return gyms[a].length - gyms[b].length;
        }
      });

      for (const gym of gymKeys) {
        const games = gyms[gym];
        const awayGames = gym.length === 0;
        if (awayGames) {
          // Away games
          output.push(renderGymDivider(t('away_games')));
        } else {
          output.push(renderGymDivider(gym));
        }

        output.push(
          <CustomList
            key={'list-season-planner-' + currentWeek?.weekAndYear + '-' + gym}
            dataSource={games}
            renderItem={(item: BasketballSeasonPlanerGame) => (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => onGameClick(item)}
              >
                <SeasonPlanerListItemComponent
                  key={item.id}
                  game={item}
                  away={awayGames}
                />
              </div>
            )}
          />
        );
      }
    }

    return output;
  };

  return (
    <div>
      <Row justify="space-between">
        <Col xs={24} sm={16}>
          <Space>
            <Button
              disabled={seasonPlanerState.loading}
              onClick={() => onPreviousButtonClick()}
            >
              {t('prev')}
            </Button>
            {renderWeekOfYears()}
            <Button
              disabled={seasonPlanerState.loading}
              onClick={() => onNextButtonClick()}
            >
              {t('next')}
            </Button>
            <Button
              disabled={seasonPlanerState.loading}
              onClick={() => onCurrentButtonClick()}
            >
              {t('gameday_planer.current_week')}
            </Button>
            <Select
              style={{ minWidth: 200 }}
              mode="multiple"
              options={tasksSelectOptions}
              placeholder={t('tasks.plural')}
              loading={savingTaskIds}
              disabled={savingTaskIds}
              value={selectedTaskIds}
              onChange={(values: number[]) => setSelectedTaskIds(values)}
              onBlur={(values) => updateSelectedTaskIds()}
            />
          </Space>
        </Col>
        {/*
        <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
          <Space>
            <Typography.Text>{t('sorting')}:</Typography.Text>
            <Radio.Group disabled defaultValue="dbb">
              <Radio.Button value="dbb">{t('association')}</Radio.Button>
              <Radio.Button value="plan">
                {t('gameday_planer.own_times')}
              </Radio.Button>
            </Radio.Group>
          </Space>
        </Col>
        */}
      </Row>
      {seasonPlanerState.loading ? (
        <Row align="middle" justify="center" style={{ height: '70vh' }}>
          <Col xs={2}>
            <Spin size="large" />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col xs={24}>{renderDays()}</Col>
        </Row>
      )}
    </div>
  );
}
